import React from "react"
import type { PropsWithChildren } from "react"
// eslint override used as this is the one place where we need to use next/link
// eslint-disable-next-line no-restricted-imports
import type { LinkProps } from "next/link"
// eslint-disable-next-line no-restricted-imports
import NextLink from "next/link"

import isAbsoluteUrl from "is-absolute-url"
import { useRouter } from "lib/router"
import { useZendeskTracking } from "hooks/useZendeskTracking"
import { useParams } from "next/navigation"

export type BPLinkProps = LinkProps & {
  href: string
  onClick?: (...args) => void
  inline?: boolean
  useDefaultLocale?: boolean
  className?: string
  target?: string
} & PropsWithChildren

export const BPLink = React.forwardRef(
  (
    {
      inline = false,
      children,
      useDefaultLocale = true,
      onClick = undefined,
      className = "",
      target = "self",
      ...props
    }: BPLinkProps,
    ref: React.Ref<HTMLAnchorElement>
  ): JSX.Element => {
    const params = useParams()
    const store = params?.store ?? "en"

    const { href: rawHref = "" } = props
    let href = rawHref

    const router = useRouter()

    href = useZendeskTracking(href)

    if (router && useDefaultLocale) {
      if (!isAbsoluteUrl(rawHref) && store !== "en") {
        href = rawHref.replace(/^\/?/, `/${store}/`)
      }
    }

    const content = inline ? <>{children}</> : children
    const dataTestId = props["data-testid"] || "Link"

    const onClickTracking = (...args) => {
      if (onClick) onClick(...args)
    }

    return (
      <NextLink
        {...props}
        ref={ref}
        data-testid={dataTestId}
        target={target}
        href={href}
        onClick={onClickTracking}
        rel="noopener noreferrer"
        className={className}
      >
        {content}
      </NextLink>
    )
  }
)
